import { $isListItemNode, $isListNode, ListNode } from '@lexical/list';
import { $isMarkNode, $unwrapMarkNode } from '@lexical/mark';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, ParagraphNode } from 'lexical';
import { useEffect } from 'react';
import { DefaultCustomStyle, DefaultLineHeight, DefaultTextAlignment } from 'const';
import { $createCustomParagraphNode, $isCustomParagraphNode, CustomParagraphNode } from '../nodes/CustomParagraphNode';
import { $isReferenceCitationNode } from '../nodes/ReferenceCitationNode';

const DEFAULT_FONT_SIZE = DefaultCustomStyle.FONT_SIZE;
const DEFAULT_LINE_HEIGHT = DefaultLineHeight;
const DEFAULT_TEXT_ALIGN = DefaultTextAlignment;

const createParagraphsFromList = (listNode: ListNode): ParagraphNode[] => {
  const paragraphs: ParagraphNode[] = [];
  const listItems = listNode.getChildren();

  listItems.forEach((listItemNode) => {
    if (!$isListItemNode(listItemNode)) {
      return;
    }
    const paragraph = $createCustomParagraphNode(DEFAULT_LINE_HEIGHT, DEFAULT_FONT_SIZE);
    const listItemChildren = listItemNode.getChildren();

    listItemChildren.forEach((node) => {
      if ($isListNode(node)) {
        paragraphs.push(...createParagraphsFromList(node));
      } else {
        paragraph.append(node);
      }
    });
    if (!paragraph.isEmpty()) {
      paragraphs.push(paragraph);
    }
  });

  return paragraphs;
};

const $removeListAndInsertParagraphs = (listNode: ListNode): void => {
  const paragraphs = createParagraphsFromList(listNode);
  paragraphs.forEach(paragraph => listNode.insertBefore(paragraph));
  listNode.remove();
};

const $resetParagraphStyle = (node: CustomParagraphNode): void => {
  node.setLineHeight(DEFAULT_LINE_HEIGHT);
  node.setFontSize(DEFAULT_FONT_SIZE);
  node.setFormat(DEFAULT_TEXT_ALIGN);
};

export const ResetStylesPlugin: React.FunctionComponent = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      const textNodes = root.getAllTextNodes();
      const markNodes = textNodes.map(node => node.getParent()).filter($isMarkNode);
      markNodes.forEach($unwrapMarkNode);
      textNodes.filter($isReferenceCitationNode).forEach(node => node.setNumber(undefined));
      const children = root.getChildren();
      children.filter($isListNode).forEach($removeListAndInsertParagraphs);
      children.filter($isCustomParagraphNode).forEach($resetParagraphStyle);
    });
  }, [editor]);

  return null;
};
